import { Button, Card, Chip, Code, Input, Label, TextField } from '@heroui/react'
import { Icon } from '@iconify/react'
import { useCallback, useEffect, useState } from 'react'

const API = 'https://api.clotho.moira-e.com'

/* ---- minimal bilingual dict (matches the fleet's clotho_lang key) ---- */
const zh = {
  title: 'clotho 运营后台',
  tokenPrompt: '输入管理令牌',
  tokenHint: 'ADMIN_API_TOKEN —— 仅存于本浏览器会话。',
  enter: '进入',
  badToken: '令牌无效',
  signOut: '退出',
  tabs: { kyb: 'KYB 审核', tenants: '租户', sanctions: '制裁告警' },
  kyb: {
    empty: '队列为空 —— 没有待审核的提交。',
    notesPlaceholder: '备注(可选,拒绝时建议填写)',
    approve: '批准',
    reject: '拒绝',
    country: '注册地',
    type: '行业',
    website: '网站',
    contact: '联系方式',
    state: '当前状态',
  },
  tenants: {
    empty: '还没有租户。',
    colTenant: '租户',
    colState: '状态',
    colCreated: '注册时间',
    setState: '设置状态',
  },
  sanctions: {
    empty: '没有未处理的告警。',
    ack: '确认处理',
    payment: '支付',
  },
  loadFailed: '加载失败',
  actionFailed: '操作失败',
}
const en: typeof zh = {
  title: 'clotho admin',
  tokenPrompt: 'Enter admin token',
  tokenHint: 'ADMIN_API_TOKEN — kept in this browser session only.',
  enter: 'Enter',
  badToken: 'Invalid token',
  signOut: 'Sign out',
  tabs: { kyb: 'KYB review', tenants: 'Tenants', sanctions: 'Sanctions alerts' },
  kyb: {
    empty: 'Queue is empty — nothing awaiting review.',
    notesPlaceholder: 'Notes (optional; recommended when rejecting)',
    approve: 'Approve',
    reject: 'Reject',
    country: 'Country',
    type: 'Business',
    website: 'Website',
    contact: 'Contact',
    state: 'Current state',
  },
  tenants: {
    empty: 'No tenants yet.',
    colTenant: 'Tenant',
    colState: 'State',
    colCreated: 'Created',
    setState: 'Set state',
  },
  sanctions: {
    empty: 'No open alerts.',
    ack: 'Acknowledge',
    payment: 'payment',
  },
  loadFailed: 'Failed to load',
  actionFailed: 'Action failed',
}

type Lang = 'zh' | 'en'
function pickLang(): Lang {
  try {
    const v = localStorage.getItem('clotho_lang')
    if (v === 'zh' || v === 'en') return v
  } catch {
    /* no storage */
  }
  return navigator.language.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

/* ---- API plumbing ----------------------------------------------------- */

function getToken(): string | null {
  try {
    return sessionStorage.getItem('clotho_admin_token')
  } catch {
    return null
  }
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${getToken() ?? ''}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  if (res.status === 401) {
    sessionStorage.removeItem('clotho_admin_token')
    window.location.reload()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/* ---- types ------------------------------------------------------------ */

interface KYBItem {
  id: string
  tenant_id: string
  tenant_slug: string
  tenant_state: string
  legal_name: string
  registration_no?: string
  country: string
  business_type: string
  website?: string
  contact: Record<string, string>
  created_at: string
}
interface TenantRow {
  id: string
  slug: string
  legal_name: string
  email: string
  state: string
  created_at: string
}
interface AlertRow {
  id: string
  tenant_slug: string
  payment_id: string
  address: string
  detail: string
  created_at: string
}

const TENANT_STATES = [
  'pending_kyb',
  'kyb_pending',
  'kyb_approved',
  'sandbox_active',
  'live_active',
  'suspended',
  'closed',
] as const

const stateTone: Record<string, 'default' | 'success' | 'warning' | 'danger'> = {
  live_active: 'success',
  sandbox_active: 'success',
  kyb_approved: 'default',
  pending_kyb: 'warning',
  kyb_pending: 'warning',
  suspended: 'danger',
  closed: 'danger',
}

/* ---- app -------------------------------------------------------------- */

export function App() {
  const [lang, setLang] = useState<Lang>(() => {
    const l = pickLang()
    document.documentElement.lang = l
    return l
  })
  const t = lang === 'zh' ? zh : en
  const [authed, setAuthed] = useState(() => getToken() !== null)

  if (!authed) {
    return <Login t={t} onAuthed={() => setAuthed(true)} />
  }
  return (
    <Shell
      t={t}
      lang={lang}
      onToggleLang={() => {
        const next: Lang = lang === 'zh' ? 'en' : 'zh'
        try {
          localStorage.setItem('clotho_lang', next)
        } catch {
          /* no storage */
        }
        document.documentElement.lang = next
        setLang(next)
      }}
      onSignOut={() => {
        sessionStorage.removeItem('clotho_admin_token')
        setAuthed(false)
      }}
    />
  )
}

function Login({ t, onAuthed }: { t: typeof zh; onAuthed: () => void }) {
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!token.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(`${API}/admin/v1/tenants`, {
        headers: { Authorization: `Bearer ${token.trim()}` },
      })
      if (!res.ok) throw new Error(String(res.status))
      sessionStorage.setItem('clotho_admin_token', token.trim())
      onAuthed()
    } catch {
      setError(t.badToken)
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <Card.Header>
          <Card.Title>{t.title}</Card.Title>
          <Card.Description>{t.tokenHint}</Card.Description>
        </Card.Header>
        <form onSubmit={submit}>
          <Card.Content className="flex flex-col gap-3">
            <TextField isRequired autoFocus value={token} onChange={setToken}>
              <Label>{t.tokenPrompt}</Label>
              <Input type="password" autoComplete="off" />
            </TextField>
            {error ? <p className="text-danger text-sm">{error}</p> : null}
          </Card.Content>
          <Card.Footer className="mt-2">
            <Button type="submit" fullWidth isDisabled={busy || !token.trim()}>
              {t.enter}
            </Button>
          </Card.Footer>
        </form>
      </Card>
    </main>
  )
}

type Tab = 'kyb' | 'tenants' | 'sanctions'

function Shell({
  t,
  lang,
  onToggleLang,
  onSignOut,
}: {
  t: typeof zh
  lang: Lang
  onToggleLang: () => void
  onSignOut: () => void
}) {
  const [tab, setTab] = useState<Tab>('kyb')
  return (
    <div className="mx-auto min-h-screen w-full max-w-5xl px-6 pb-16">
      <header className="flex h-14 items-center gap-4">
        <span className="bg-accent text-accent-foreground flex size-7 items-center justify-center rounded-lg text-sm font-bold">
          C
        </span>
        <span className="text-sm font-semibold">{t.title}</span>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" onPress={onToggleLang}>
          {lang === 'zh' ? 'EN' : '中文'}
        </Button>
        <Button variant="ghost" size="sm" onPress={onSignOut}>
          {t.signOut}
        </Button>
      </header>

      <div className="mb-6 flex gap-2">
        {(['kyb', 'tenants', 'sanctions'] as const).map((k) => (
          <Button
            key={k}
            size="sm"
            variant={tab === k ? 'primary' : 'ghost'}
            onPress={() => setTab(k)}
          >
            {t.tabs[k]}
          </Button>
        ))}
      </div>

      {tab === 'kyb' ? <KYBTab t={t} /> : tab === 'tenants' ? <TenantsTab t={t} /> : <SanctionsTab t={t} />}
    </div>
  )
}

/* ---- KYB tab ----------------------------------------------------------- */

function KYBTab({ t }: { t: typeof zh }) {
  const [queue, setQueue] = useState<KYBItem[] | null>(null)
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    adminFetch<{ queue: KYBItem[] }>('/admin/v1/kyb-queue')
      .then((r) => setQueue(r.queue))
      .catch(() => setError(t.loadFailed))
  }, [t.loadFailed])

  useEffect(load, [load])

  async function review(id: string, decision: 'approved' | 'rejected') {
    try {
      await adminFetch(`/admin/v1/kyb/${id}/review`, {
        method: 'POST',
        body: JSON.stringify({ decision, notes: notes[id] ?? '' }),
      })
      load()
    } catch {
      setError(t.actionFailed)
    }
  }

  if (error) return <p className="text-danger text-sm">{error}</p>
  if (queue === null) return <p className="text-muted text-sm">…</p>
  if (queue.length === 0) return <p className="text-muted text-sm">{t.kyb.empty}</p>

  return (
    <div className="flex flex-col gap-4">
      {queue.map((item) => (
        <Card key={item.id}>
          <Card.Header>
            <div className="flex flex-wrap items-center gap-2">
              <Card.Title>{item.legal_name}</Card.Title>
              <Code className="text-xs">{item.tenant_slug}</Code>
              <Chip size="sm" color="warning">
                {item.tenant_state}
              </Chip>
              <span className="text-muted ml-auto text-xs">
                {new Date(item.created_at).toLocaleString()}
              </span>
            </div>
          </Card.Header>
          <Card.Content className="flex flex-col gap-3 text-sm">
            <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
              <Fact label={t.kyb.country} value={item.country} />
              <Fact label={t.kyb.type} value={item.business_type} />
              {item.registration_no ? <Fact label="Reg#" value={item.registration_no} /> : null}
              {item.website ? (
                <Fact
                  label={t.kyb.website}
                  value={
                    <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-accent">
                      {item.website}
                    </a>
                  }
                />
              ) : null}
              {Object.entries(item.contact ?? {}).map(([k, v]) => (
                <Fact key={k} label={`${t.kyb.contact} · ${k}`} value={v} />
              ))}
            </div>
            <TextField value={notes[item.id] ?? ''} onChange={(v) => setNotes((m) => ({ ...m, [item.id]: v }))}>
              <Input placeholder={t.kyb.notesPlaceholder} />
            </TextField>
          </Card.Content>
          <Card.Footer className="flex justify-end gap-2">
            <Button variant="danger-soft" size="sm" onPress={() => void review(item.id, 'rejected')}>
              {t.kyb.reject}
            </Button>
            <Button size="sm" onPress={() => void review(item.id, 'approved')}>
              <Icon icon="gravity-ui:check" className="mr-1 size-4" />
              {t.kyb.approve}
            </Button>
          </Card.Footer>
        </Card>
      ))}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted shrink-0">{label}:</span>
      <span className="min-w-0 break-all">{value}</span>
    </div>
  )
}

/* ---- Tenants tab -------------------------------------------------------- */

function TenantsTab({ t }: { t: typeof zh }) {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    adminFetch<{ tenants: TenantRow[] }>('/admin/v1/tenants')
      .then((r) => setTenants(r.tenants))
      .catch(() => setError(t.loadFailed))
  }, [t.loadFailed])

  useEffect(load, [load])

  async function setState(id: string, state: string) {
    try {
      await adminFetch(`/admin/v1/tenants/${id}/state`, {
        method: 'POST',
        body: JSON.stringify({ state }),
      })
      load()
    } catch {
      setError(t.actionFailed)
    }
  }

  if (error) return <p className="text-danger text-sm">{error}</p>
  if (tenants === null) return <p className="text-muted text-sm">…</p>
  if (tenants.length === 0) return <p className="text-muted text-sm">{t.tenants.empty}</p>

  return (
    <Card>
      <Card.Content className="p-0">
        <table className="w-full text-sm">
          <thead className="text-muted text-xs">
            <tr className="border-border border-b">
              <th className="px-4 py-3 text-left font-medium">{t.tenants.colTenant}</th>
              <th className="px-4 py-3 text-left font-medium">{t.tenants.colState}</th>
              <th className="px-4 py-3 text-left font-medium">{t.tenants.colCreated}</th>
              <th className="px-4 py-3 text-left font-medium">{t.tenants.setState}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tn) => (
              <tr key={tn.id} className="border-border border-b align-top last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium">{tn.legal_name}</div>
                  <div className="text-muted text-xs">
                    {tn.slug} · {tn.email}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Chip size="sm" color={stateTone[tn.state] ?? 'default'}>
                    {tn.state}
                  </Chip>
                </td>
                <td className="text-muted px-4 py-3 text-xs">
                  {new Date(tn.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <select
                    className="border-border bg-surface rounded-lg border px-2 py-1 text-xs"
                    value={tn.state}
                    onChange={(e) => void setState(tn.id, e.target.value)}
                  >
                    {TENANT_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card.Content>
    </Card>
  )
}

/* ---- Sanctions tab ------------------------------------------------------ */

function SanctionsTab({ t }: { t: typeof zh }) {
  const [alerts, setAlerts] = useState<AlertRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    adminFetch<{ alerts: AlertRow[] }>('/admin/v1/sanctions-alerts')
      .then((r) => setAlerts(r.alerts))
      .catch(() => setError(t.loadFailed))
  }, [t.loadFailed])

  useEffect(load, [load])

  async function ack(id: string) {
    try {
      await adminFetch(`/admin/v1/sanctions-alerts/${id}/ack`, { method: 'POST' })
      load()
    } catch {
      setError(t.actionFailed)
    }
  }

  if (error) return <p className="text-danger text-sm">{error}</p>
  if (alerts === null) return <p className="text-muted text-sm">…</p>
  if (alerts.length === 0) return <p className="text-muted text-sm">{t.sanctions.empty}</p>

  return (
    <div className="flex flex-col gap-3">
      {alerts.map((a) => (
        <Card key={a.id} className="border-danger/30">
          <Card.Content className="flex flex-wrap items-center gap-3 text-sm">
            <Icon icon="gravity-ui:triangle-exclamation" className="text-danger size-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{a.detail}</div>
              <div className="text-muted mt-0.5 text-xs">
                {a.tenant_slug} · {t.sanctions.payment} <Code className="text-xs">{a.payment_id}</Code> ·{' '}
                {new Date(a.created_at).toLocaleString()}
              </div>
            </div>
            <Button size="sm" variant="outline" onPress={() => void ack(a.id)}>
              {t.sanctions.ack}
            </Button>
          </Card.Content>
        </Card>
      ))}
    </div>
  )
}
